
import { supabase } from "@/integrations/supabase/client";
import { extractVehicleInfo, type VehicleInfo, type VehicleExtractionResult } from "./estimates/vehicleExtractor";
import { extractRepairLines, type RepairSection } from "./estimates/repairLineExtractor";
import { extractAdasOperations, type AdasOperation } from "./estimates/adasOperationExtractor";
import { type ProcessedEstimate, type RepairLines } from "./estimates/types";

interface EstimateData {
  vehicle?: {
    vin?: string;
  };
  vin?: string;
  roNumber?: string;
  repairLines?: RepairLines;
}

export interface EstimateAnalysisResults {
  processedEstimate: ProcessedEstimate | null;
  confidence: {
    overall: number;
    sections: {
      vehicle: number;
      repair: number;
      adas: number;
    };
  };
  extractionStats: {
    total: number;
    successfullyProcessed: number;
    missingFields: string[];
    partialExtractions: number;
  };
  errors?: {
    vehicle?: string[];
    repair?: string[];
    adas?: string[];
  };
  extractionLogs?: any[];
}

export const analyzeEstimate = async (estimateId: string): Promise<EstimateAnalysisResults> => {
  const startTime = performance.now();
  const logs: any[] = [];
  
  const log = (message: string, data?: any) => {
    const entry = {
      timestamp: new Date().toISOString(),
      estimateId,
      step: 'estimate_analysis',
      message,
      ...(data && { data })
    };
    logs.push(entry);
    console.log(`[Estimate Analysis] ${message}`, data || '');
  };

  log('Starting estimate analysis', { estimateId });

  const { data: estimate, error } = await supabase
    .from('estimates')
    .select('*')
    .eq('id', estimateId)
    .single();

  if (error || !estimate) {
    log('Failed to fetch estimate', { error });
    throw new Error('Failed to fetch estimate');
  }

  const text = estimate.extracted_text;
  if (!text) {
    log('No extracted text found');
    throw new Error('No extracted text found in estimate');
  }

  // Handle processed_data parsing
  let processedData: EstimateData = {};
  if (typeof estimate.processed_data === 'string') {
    try {
      processedData = JSON.parse(estimate.processed_data) as EstimateData;
      log('Parsed processed data', { processedData });
    } catch (e) {
      log('Error parsing processed data', { error: e });
      throw new Error('Invalid processed data format');
    }
  } else if (estimate.processed_data && typeof estimate.processed_data === 'object') {
    processedData = estimate.processed_data as EstimateData;
    log('Using existing processed data', { processedData });
  }

  // Extract vehicle info with logs
  const vehicleResult = extractVehicleInfo(text);
  // Safely append logs if they exist
  if (vehicleResult.logs) {
    logs.push(...vehicleResult.logs);
  }

  // Calculate confidence scores
  const confidence = {
    overall: 0.8, // Default confidence score
    sections: {
      vehicle: processedData.vehicle?.vin ? 1 : 0,
      repair: processedData.repairLines?.rows?.length > 0 ? 1 : 0,
      adas: 0.5 // Default ADAS confidence
    }
  };

  // Calculate totals from repair lines
  const totalOperations = processedData.repairLines?.rows?.length || 0;
  const validOperations = processedData.repairLines?.rows?.filter(op => op.operation && op.description).length || 0;

  // Process repair lines
  const laborTotals = {
    body: 0,
    refinish: 0,
    mechanical: 0
  };

  processedData.repairLines?.rows?.forEach(op => {
    switch (op.laborType?.toLowerCase()) {
      case 'body':
        laborTotals.body += 1;
        break;
      case 'paint':
      case 'refinish':
        laborTotals.refinish += 1;
        break;
      case 'mechanical':
      case 'mech':
        laborTotals.mechanical += 1;
        break;
    }
  });

  const duration = performance.now() - startTime;
  log('Analysis complete', { 
    duration: `${duration.toFixed(2)}ms`,
    confidence,
    laborTotals
  });

  const processedEstimate: ProcessedEstimate = {
    vehicle: {
      vin: processedData.vin || estimate.vin || ''
    },
    roNumber: processedData.roNumber || estimate.ro_number || null,
    repairLines: processedData.repairLines || { headers: [], rows: [] }
  };

  return {
    processedEstimate,
    confidence,
    extractionStats: {
      total: totalOperations,
      successfullyProcessed: validOperations,
      missingFields: [],
      partialExtractions: totalOperations - validOperations
    },
    extractionLogs: logs
  };
};
